/* The scope switch over the master pane — TABS, not a segmented track.
 *
 * ── Why this left the canon (owner, 16 September 2026) ───────────────────────────────────────
 * The canon draws a pill inside a pill: a track, a filled segment inside it, and inside that the
 * word and a counted pill. Four edges for a two-state control, and the filled segment reads as a
 * button that does something rather than as the state the list is already in. The owner asked for
 * the control to stop shouting, and chose tabs with a rule under the active one. Deviation D30.
 *
 * ── The rule was WHITE, and the owner overruled it ───────────────────────────────────────────
 * The argument this file used to make: colour on this screen means "what kind of plan this is" —
 * the row's cover rule, the inspector's chip and the pattern week's band all read `--cat-ink` — so
 * an ember rule would put the accent's meaning in two places a few pixels apart. The owner asked
 * on 16 September 2026 for one ember underline across every tab bar in the app regardless, and the
 * rule has read `--accent-grad-rule` since. The white ink of the selected WORD still carries the
 * state beside it.
 *
 * ── The box keeps the canon's height, so nothing under it moves ──────────────────────────────
 * `--h-pl-tabs` is the track's own outer height (3 + 25.5 + 3 plus its two hairlines). The list
 * below is laid out against it, and a control that changed shape would move every row.
 *
 * The tabs STRETCH to fill that height and centre their own 10px word inside it, rather than
 * sitting on the bottom edge with padding above them: a 13px line box pinned to the foot of a
 * 31.5px row reads as text that has slipped, and the rule under it lands in the same place either
 * way — it is drawn against the track's border, not against the word.
 *
 * ── ONE weight in both states ────────────────────────────────────────────────────────────────
 * The canon's options are `flex: 1`, so its 600 → 800 swap changes nothing about the layout. Tabs
 * are content-width, so the same swap would widen the active tab and slide the one beside it every
 * time the trainer switched scope. The state is carried by ink and by the rule instead.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

/* A `<fieldset>` needs its user-agent frame taken off before it can be a tab row: the UA gives it a
 * 2px groove border, `padding-block-start: 0.35em` and a `min-inline-size: min-content` that
 * refuses to shrink under the widest option.
 *
 * Three sides of that border stay, TRANSPARENT, rather than being removed: the row's height is
 * stated and a border is part of it, so dropping three hairlines would move the list under it by
 * two pixels for no visible reason. The ink arrives as a custom property because Stylelint's value
 * allowlist takes a token reference and nothing else, and `transparent` is not one — a custom
 * property is never read by that gate, which is exactly the seam it leaves for a value like this. */
.track {
  --track-edge: transparent;

  display: flex;
  flex: none;
  gap: var(--space-17-5px);
  align-items: stretch;
  min-inline-size: var(--space-0);
  height: var(--h-pl-tabs);
  margin: var(--space-10-5px) var(--space-13px) var(--space-0);
  padding: var(--space-0);
  border: var(--border-hairline) solid var(--track-edge);
  border-bottom-color: var(--border);
}

.option {
  position: relative;
  display: inline-flex;
  gap: var(--space-5-5px);
  align-items: center;
  padding: var(--space-0);
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-heading);
  color: var(--text-3);
  white-space: nowrap;
  background: transparent;
  cursor: pointer;

  /* The canon's `color .18s ease`. Only the ink: the rule under the active tab is a different
   * element and fades on its own, and nothing else about the box is allowed to move. */
  transition-duration: var(--dur-swap);
  transition-property: color;
  transition-timing-function: var(--ease);
}

.optionSelected {
  color: var(--text);
}

/* ── The underline is `shared/ui/tabRule.module.css`, and only its BOX is here ────────────────
 * The fill, the height, the resting `scaleX(0)` and the transition that opens it are the app's one
 * tab underline — owner, 18 September 2026, after asking on 16 September for one ember treatment
 * across every tab bar and then for the mockup's movement to go with it. This was a static
 * `::after` on the chosen option until then: it appeared and vanished rather than growing.
 *
 * What is left here is where the rule sits and the corners the canon rounds. Over the row's own
 * hairline rather than above it, so the two read as one line broken by the active tab instead of as
 * two rules a pixel apart. Nothing else about the rule may be written here. */
.optionRule {
  bottom: calc(var(--border-hairline) * -1);
  border-radius: var(--radius-hair) var(--radius-hair) var(--space-0) var(--space-0);
}

/* The radio itself is off screen rather than `display: none`, which would take it out of the
 * accessibility tree and with it the group's whole keyboard contract. The label around it is the
 * control a trainer sees, so the ring is drawn there. */
.input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  white-space: nowrap;
  clip-path: inset(50%);
}

.option:hover {
  color: var(--text);
}

.option:has(.input:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The count beside the word. Its BOX stands from the first frame and its ink arrives with the read
 * — see the component — so this never has to answer for a zero it cannot vouch for and the word
 * beside it never slides.
 *
 * ── The reserve is two DIGITS, drawn and never painted ───────────────────────────────────────
 * `::before { content: '00' }` in the same grid cell as the figure, so the box is at least as wide
 * as two tabular digits in whatever face is actually loaded. Measured alternatives, both wrong:
 * a fixed `min-width` lets a two-digit count widen it and slide the tab beside it, and a `2ch`
 * minimum is computed against the CURRENT font, so it changes again when the webfont swaps in —
 * a second jump, on a different clock.
 *
 * The canon's pill is gone with the track that justified it: a filled counter inside a tab would
 * put back the nested-box reading the owner asked to remove. It is a plain number now: 12 / 500
 * beside the 13px word (owner's polish, 4 November 2026). */
.count {
  display: inline-grid;
  place-items: center;
  font-size: var(--type-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-none);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

/* Two digits' worth of box, laid out and never painted, in the same cell as the figure. */
.count::before {
  grid-area: 1 / 1;
  content: '00';
  visibility: hidden;
}

.countFigure {
  grid-area: 1 / 1;
  font-weight: inherit;
}

/* The reserve without the figure: the box is laid out, nothing is painted, and neither the
 * accessibility tree nor the screen has anything in it. */
.countWaiting {
  visibility: hidden;
}

.optionSelected .count {
  color: var(--text-2);
}
